import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { RiLightbulbLine } from "react-icons/ri";
import { AiOutlineUpload } from "react-icons/ai";
import { FiTrash } from "react-icons/fi";

import { compressImage } from "../tool";
import * as api from "@api/api";
import { warning, error } from "@alert";

const EditPicture = ({ form, setForm, setData, getListFromS3 }) => {
    const token = useSelector((state) => state.user.user.token);
    const [dragStates, setDragStates] = useState({
        main: false,
        list: false,
        replace: {}
    });
    const [imageErrors, setImageErrors] = useState({});
    const [uploading, setUploading] = useState(false);

    // 當圖片列表改變時，清除不存在圖片的錯誤狀態
    useEffect(() => {
        setImageErrors(prev => {
            const newErrors = {};
            form.branch_images.forEach((_, index) => {
                if (prev[index] !== undefined) {
                    newErrors[index] = prev[index];
                }
            });
            return newErrors;
        });
    }, [form.branch_images]);

    const handleImageChange = async (e) => {
        const maxFileSize = 2.5 * 1024 * 1024;
        const isEvent = e && e.target && e.target.files;
        const file = isEvent ? e.target.files[0] : e;
        if (isEvent) e.target.value = '';

        if (file) {
            const validTypes = ['image/jpg', 'image/jpeg', 'image/png'];
            if (!validTypes.includes(file.type)) {
                warning(1, "檔案格式錯誤", "僅限 JPG, JPEG, PNG 格式", "我了解了");
                return;
            }

            if (file.size < maxFileSize) {
                try {
                    setImageErrors({});
                    setUploading(true);
                    const reSizeImage = await compressImage(file, 400, 0.8);
                    try {
                        await getListFromS3(reSizeImage);
                        setUploading(false);
                    } catch (uploadErr) {
                        setUploading(false);
                        error(1, "上傳失敗", `${uploadErr.message}`, "確認");
                    }
                } catch (err) {
                    setUploading(false);
                    error(1, "圖片處理失敗", `${err.message}`, "確認");
                }
            } else {
                warning(1, "檔案壓縮後仍超過 2.5 MB", "請自行壓縮再上傳！", "我了解了");
            }
        }
    };

    // 處理取代圖片（上傳到 S3，然後更新對應索引）
    const handleReplaceImage = async (e, index) => {
        const maxFileSize = 2.5 * 1024 * 1024;
        const isEvent = e && e.target && e.target.files;
        const file = isEvent ? e.target.files[0] : e;
        if (isEvent) e.target.value = '';

        if (file) {
            // 驗證檔案類型
            const validTypes = ['image/jpg', 'image/jpeg', 'image/png'];
            if (!validTypes.includes(file.type)) {
                warning(1, "檔案格式錯誤", "僅限 JPG, JPEG, PNG 格式", "我了解了");
                return;
            }

            if (file.size < maxFileSize) {
                try {
                    setImageErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors[index];
                        return newErrors;
                    });
                    setUploading(true);
                    const reSizeImage = await compressImage(file, 400, 0.8);
                    const res = await api.convertArrayToUrlList(token, reSizeImage);
                    if (res.data.status === 'success') {
                        const newImageList = [...form.branch_images];
                        newImageList[index] = res.data.data;
                        setForm((data) => ({
                            ...data,
                            "branch_images": newImageList
                        }));
                        setData((list) => ({
                            ...list,
                            "branch_images": newImageList
                        }));
                        setUploading(false);
                    } else {
                        setUploading(false);
                        error(1, "上傳失敗", "請稍後重試", "確認");
                    }
                } catch (err) {
                    setUploading(false);
                    error(1, "上傳失敗", `${err.message}`, "確認");
                }
            } else {
                warning(1, "檔案壓縮後仍超過 2.5 MB", "請壓縮後再上傳！", "我了解了");
            }
        }
    };

    const removeImage = (index) => {
        const newImageList = form.branch_images.slice(0, index).concat(form.branch_images.slice(index + 1));
        setForm((data) => ({
            ...data,
            "branch_images": newImageList
        }));
        setData((list) => ({
            ...list,
            "branch_images": newImageList
        }))
    };

    // Drag and Drop handlers
    const handleDragEnter = (e, type, index = null) => {
        e.preventDefault();
        e.stopPropagation();
        if (index !== null) {
            setDragStates(prev => ({
                ...prev,
                replace: { ...prev.replace, [index]: true }
            }));
        } else {
            setDragStates(prev => ({ ...prev, [type]: true }));
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDragLeave = (e, type, index = null) => {
        e.preventDefault();
        e.stopPropagation();
        if (index !== null) {
            setDragStates(prev => ({
                ...prev,
                replace: { ...prev.replace, [index]: false }
            }));
        } else {
            setDragStates(prev => ({ ...prev, [type]: false }));
        }
    };

    const handleDrop = async (e, type, index = null) => {
        e.preventDefault();
        e.stopPropagation();
        
        // 使用原生事件對象確保 dataTransfer 可用
        const nativeEvent = e.nativeEvent || e;
        const dataTransfer = nativeEvent.dataTransfer;
        
        // 確保 dataTransfer 存在
        if (!dataTransfer) {
            console.log('dataTransfer is undefined', { nativeEvent, e });
            return;
        }
        
        if (index !== null) {
            setDragStates(prev => ({
                ...prev,
                replace: { ...prev.replace, [index]: false }
            }));
            // 取代圖片
            const files = dataTransfer.files;
            if (files && files.length > 0) {
                await handleReplaceImage(files[0], index);
            }
        } else {
            setDragStates(prev => ({ ...prev, [type]: false }));
            // 新增圖片
            const files = dataTransfer.files;
            if (files && files.length > 0) {
                await handleImageChange(files[0]);
            }
        }
    };

    const handleImageError = (index) => {
        setImageErrors(prev => ({ ...prev, [index]: true }));
    };

    const renderImageItem = (image, index) => {
        const imageIndex = index + 1;
        return (
            <li key={index}>
                <div 
                    className={`image-hover-container ${dragStates.replace[imageIndex] ? 'drag-over' : ''}`}
                    onDragEnter={(e) => handleDragEnter(e, null, imageIndex)}
                    onDragOver={handleDragOver}
                    onDragLeave={(e) => handleDragLeave(e, null, imageIndex)}
                    onDrop={(e) => handleDrop(e, null, imageIndex)}
                >
                    <img
                        src={image} 
                        alt="圖片載入失敗" 
                        className="preview-image" 
                        onError={() => handleImageError(imageIndex)}
                        style={{ display: imageErrors[imageIndex] ? 'none' : 'block' }}
                    />
                    {imageErrors[imageIndex] && (
                        <div className="preview-image image-error-placeholder">
                            <span>圖片載入失敗</span>
                        </div>
                    )}
                    <div className="image-overlay">
                    <input
                        id={`replace-image-${index + 1}`}
                        type="file"
                        accept="image/jpg,image/jpeg,image/png"
                        onChange={(e) => handleReplaceImage(e, index + 1)}
                        style={{ display: 'none' }}
                    />
                    <button
                        type="button"
                        className="overlay-button replace-button"
                        onClick={() => document.getElementById(`replace-image-${index + 1}`).click()}
                    >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M17 8L12 3L7 8" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M12 3V15" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <span>取代</span>
                    </button>
                    <button
                        type="button"
                        className="overlay-button delete-button"
                        onClick={() => removeImage(index + 1)}
                    >
                        <FiTrash size={24} />
                    </button>
                </div>
            </div>
        </li>
        );
    };

    return (
        <div className="edit-warp-content-picture">
            <div className="edit-warp-content-picture-main">
                {form.branch_images.length === 0 ? (
                    uploading ? (
                        <div className="upload-label">
                            <div className="upload-area upload-area--loading upload-area--match-picture-main">
                                <div className="upload-spinner" />
                                <p style={{ marginTop: 12, color: '#888' }}>上傳中...</p>
                            </div>
                        </div>
                    ) : (
                    <div className="upload-label">
                        <input 
                            id="main-upload-input"
                            type="file" 
                            accept="image/jpg,image/jpeg,image/png" 
                            onChange={handleImageChange}
                            style={{ display: 'none' }}
                        />
                        <div 
                            className={`upload-area ${dragStates.main ? 'drag-over' : ''}`}
                            onClick={() => document.getElementById('main-upload-input').click()}
                            onDragEnter={(e) => handleDragEnter(e, 'main')}
                            onDragOver={handleDragOver}
                            onDragLeave={(e) => handleDragLeave(e, 'main')}
                            onDrop={(e) => handleDrop(e, 'main')}
                        >
                            <AiOutlineUpload size={40} />
                            <p>上傳照片</p>
                            <p>僅限 JPG, JPEG, PNG 格式</p>
                            <p style={{ marginTop: '8px', fontSize: '12px', opacity: 0.7 }}>或拖放圖片至此處</p>
                        </div>
                    </div>
                    )
                ) : (
                    <div className="edit-warp-content-picture-main-pic">
                        <div 
                            className={`image-hover-container ${dragStates.replace[0] ? 'drag-over' : ''}`}
                            onDragEnter={(e) => handleDragEnter(e, null, 0)}
                            onDragOver={handleDragOver}
                            onDragLeave={(e) => handleDragLeave(e, null, 0)}
                            onDrop={(e) => handleDrop(e, null, 0)}
                        >
                            <img
                                src={form.branch_images[0]} 
                                alt="圖片載入失敗" 
                                className="preview-image" 
                                onError={() => handleImageError(0)}
                                style={{ display: imageErrors[0] ? 'none' : 'block' }}
                            />
                            {imageErrors[0] && (
                                <div className="preview-image image-error-placeholder">
                                    <span>圖片載入失敗</span>
                                </div>
                            )}
                            <div className="image-overlay">
                                <input
                                    id="replace-main-image"
                                    type="file"
                                    accept="image/jpg,image/jpeg,image/png"
                                    onChange={(e) => handleReplaceImage(e, 0)}
                                    style={{ display: 'none' }}
                                />
                                <button
                                    type="button"
                                    className="overlay-button replace-button"
                                    onClick={() => document.getElementById('replace-main-image').click()}
                                >
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        <path d="M17 8L12 3L7 8" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        <path d="M12 3V15" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                    <span>取代</span>
                                </button>
                                <button
                                    type="button"
                                    className="overlay-button delete-button"
                                    onClick={() => removeImage(0)}
                                >
                                    <FiTrash size={24} />
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {form.branch_images.length <= 0 ? (
                <div className="edit-warp-content-picture-warning">
                    <RiLightbulbLine size={24} />
                    <p>需要上傳最少 1 張照片，最多 5 張照片</p>
                </div>
            ) : (
                <>
                    <ul className="edit-warp-content-picture-list">
                        {form.branch_images.slice(1).map(renderImageItem)}
                        {form.branch_images.length < 5 && (
                            <li>
                                {uploading ? (
                                    <div className="upload-label">
                                        <div className="upload-area upload-area--loading">
                                            <div className="upload-spinner" />
                                            <p style={{ marginTop: 12, color: '#888' }}>上傳中...</p>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="upload-label">
                                        <input 
                                            id="list-upload-input"
                                            type="file" 
                                            accept="image/jpg,image/jpeg,image/png" 
                                            onChange={handleImageChange}
                                            style={{ display: 'none' }}
                                        />
                                        <div 
                                            className={`upload-area ${dragStates.list ? 'drag-over' : ''}`}
                                            onClick={() => document.getElementById('list-upload-input').click()}
                                            onDragEnter={(e) => handleDragEnter(e, 'list')}
                                            onDragOver={handleDragOver}
                                            onDragLeave={(e) => handleDragLeave(e, 'list')}
                                            onDrop={(e) => handleDrop(e, 'list')}
                                        >
                                            <AiOutlineUpload size={30} />
                                            <p>上傳照片</p>
                                            <p style={{ fontSize: 11, opacity: 0.7 }}>僅限 JPG, JPEG, PNG 格式</p>
                                            <p style={{ marginTop: '4px', fontSize: '11px', opacity: 0.6 }}>或拖放圖片至此處</p>
                                        </div>
                                    </div>
                                )}
                            </li>
                        )}
                    </ul>
                    {form.branch_images.length === 5 && (
                        <div className="edit-warp-content-picture-warning edit-warp-content-picture-warning-success">
                            <RiLightbulbLine size={24} />
                            <p>相片已滿 5 張，您可刪除或取代相片</p>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default EditPicture;
